<template>
  <div v-if="series && series.length > 1" class="box evo-summary-block">
    <div class="evo-summary-head">
      <h2>Variación por variable</h2>
    </div>
    <div class="evo-summary-table-wrap">
      <table class="evo-summary-table">
        <thead>
          <tr>
            <th class="col-variable">Variable</th>
            <th
              v-for="(pt, i) in series"
              :key="`head-${i}`"
              :class="{ current: i === series.length - 1 }"
            >
              {{ yearLabel(pt) }}
            </th>
            <th class="col-trend">Tendencia</th>
            <th class="col-delta">{{ yearLabel(series[0]) }} → {{ yearLabel(series[series.length - 1]) }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in visibleRows"
            :key="row.isChild ? `${row.parentKey}-${row.key}` : row.key"
            :class="{ 'evo-child-row': row.isChild }"
          >
            <td class="col-variable">
              <button
                v-if="row.children"
                type="button"
                class="row-toggle"
                @click="toggleRow(row.key)"
              >
                <i class="material-icons chevron">{{
                  isExpanded(row.key) ? "expand_less" : "expand_more"
                }}</i>
                {{ row.label }}
              </button>
              <span v-else>{{ row.label }}</span>
            </td>
            <td
              v-for="(pt, i) in series"
              :key="`${row.key}-${i}`"
              class="col-value"
              :class="[
                `rango${rangos(valueOf(pt, row.key))}`,
                { current: i === series.length - 1 },
              ]"
            >
              {{ fmt(valueOf(pt, row.key)) }}
            </td>
            <td class="col-trend">
              <div class="col-trend-inner">
                <sparkline
                  :values="seriesValues(row.key)"
                  :domain-min="sparklineDomain.min"
                  :domain-max="sparklineDomain.max"
                  :width="60"
                  :height="22"
                />
              </div>
            </td>
            <td class="col-delta">
              <variation-chip
                :current="lastValue(row.key)"
                :previous="firstValue(row.key)"
                :size="12"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import Sparkline from "./components/sparkline.vue";
import VariationChip from "./components/variationChip.vue";

// Jerarquía padre/hijas confirmada contra ResultadosDetallado.vue
// (detailSummary.CClimad + namevars) — mismas variables que usa el tab
// Distribución para el mismo tipo de despliegue.
const ROWS = [
  { key: "CClimad", label: "Clima total" },
  {
    key: "apoyod",
    label: "Apoyo",
    children: [
      { key: "apsd", label: "Social" },
      { key: "vincd", label: "Compromiso" },
      { key: "apod", label: "Organizacional" },
    ],
  },
  {
    key: "innovaciond",
    label: "Innovación",
    children: [
      { key: "autd", label: "Autonomía" },
      { key: "agid", label: "Agilidad" },
      { key: "mejd", label: "Mejora" },
    ],
  },
  {
    key: "calidadd",
    label: "Calidad",
    children: [
      { key: "excd", label: "Excelencia" },
      { key: "cycd", label: "Claridad" },
      { key: "segd", label: "Seguridad" },
    ],
  },
  {
    key: "metasd",
    label: "Metas",
    children: [
      { key: "logrd", label: "Logro" },
      { key: "servd", label: "Servicio" },
      { key: "repd", label: "Reputación" },
    ],
  },
  { key: "lidd", label: "Liderazgo" },
  { key: "recd", label: "Recursos" },
  { key: "compd", label: "Compensación" },
  { key: "engad", label: "Engagement" },
];

export default {
  name: "EvolutionSummaryTable",

  components: {
    Sparkline,
    VariationChip,
  },

  props: {
    series: {
      type: Array,
      default: () => [],
    },

    rangos: {
      type: Function,
      required: true,
    },
  },

  data() {
    return {
      rows: ROWS,
      expanded: {},
    };
  },

  methods: {
    toggleRow(key) {
      this.$set(this.expanded, key, !this.expanded[key]);
    },

    isExpanded(key) {
      return !!this.expanded[key];
    },

    valueOf(pt, key) {
      return pt.total && pt.total[key] ? pt.total[key].p : 0;
    },

    fmt(v) {
      return v.toFixed(1).replace(".", ",");
    },

    seriesValues(key) {
      return this.series.map((pt) => this.valueOf(pt, key));
    },

    firstValue(key) {
      return this.valueOf(this.series[0], key);
    },

    lastValue(key) {
      return this.valueOf(this.series[this.series.length - 1], key);
    },

    yearLabel(pt) {
      return pt && pt.fecha ? new Date(pt.fecha).getFullYear() : "";
    },
  },

  computed: {
    visibleRows() {
      const list = [];
      this.rows.forEach((row) => {
        list.push(row);
        if (row.children && this.isExpanded(row.key)) {
          row.children.forEach((child) => {
            list.push({ ...child, isChild: true, parentKey: row.key });
          });
        }
      });
      return list;
    },

    sparklineDomain() {
      const flatKeys = this.rows.reduce((acc, row) => {
        acc.push(row.key);
        if (row.children) acc.push(...row.children.map((c) => c.key));
        return acc;
      }, []);
      const all = flatKeys.reduce(
        (acc, key) => acc.concat(this.seriesValues(key)),
        [],
      );
      if (!all.length) return { min: 0, max: 100 };
      const min = Math.min(...all);
      const max = Math.max(...all);
      const padding = Math.max((max - min) * 0.1, 1);
      return {
        min: Math.max(0, min - padding),
        max: Math.min(100, max + padding),
      };
    },
  },
};
</script>

<style scoped>
.evo-summary-block {
  margin-top: 18px;
}

.evo-summary-head h2 {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
}

.evo-summary-table-wrap {
  overflow-x: auto;
  margin-top: 14px;
}

table.evo-summary-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 3px 4px;
  font-size: 13px;
  min-width: 640px;
  font-variant-numeric: tabular-nums;
}

table.evo-summary-table th {
  text-align: right;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #55545a;
  padding: 0 10px 8px;
}

table.evo-summary-table th.col-variable,
table.evo-summary-table td.col-variable {
  text-align: left;
}

table.evo-summary-table td {
  padding: 9px 12px;
  text-align: right;
  border-radius: 5px;
}

table.evo-summary-table td.col-variable {
  font-weight: 600;
  background: #fbfaf9;
}

.row-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-weight: inherit;
  color: inherit;
  cursor: pointer;
}

.row-toggle .chevron {
  flex-shrink: 0;
  color: #9ca3af;
  font-size: 18px;
}

tr.evo-child-row td.col-variable {
  padding-left: 28px;
  font-weight: 400;
  color: #55545a;
  background: #ffffff;
}

table.evo-summary-table td.col-value.current {
  box-shadow: inset 0 0 0 2px #522687;
  font-weight: 700;
}

table.evo-summary-table td.col-trend,
table.evo-summary-table td.col-delta {
  background: #fff;
}

table.evo-summary-table .col-trend,
table.evo-summary-table .col-delta {
  display: table-cell;
  text-align: center;
}

.col-trend-inner {
  display: flex;
  justify-content: center;
}
</style>



// WEBPACK FOOTER //
// src/components/climan/evolutionSummaryTable.vue